import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { RentalInspectionPhotoList } from "./RentalInspectionPhotos";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { api, getApiErrorMessage } from "../../api/client";
import { formatInstant } from "../../lib/calendar-date";
import { fmtCurrency, parseMoney } from "../../lib/display-labels";
import type { RentalDamageCharge, RentalDamageChargeAction } from "../../types/rental-damage-charge";

/**
 * Damage charges (fieldiq-api#1037, #815 G): what a return found, charged to
 * the customer. Pending → the office prices or waives it (a reason is
 * required); a waived one can be reopened; a billed one is read-only — it is
 * on an invoice, and voiding that invoice releases it. The amount is the
 * office's; nothing here computes one. Shared by the order page and the
 * "Damage charges" queue.
 */

/** The billing-write roles (RENTAL_BILLING_WRITE on the API). */
export const DAMAGE_CHARGE_WRITE_ROLES = ["admin", "office_staff"];
/** The billing-read roles (RENTAL_BILLING_READ). */
export const DAMAGE_CHARGE_READ_ROLES = ["admin", "office_staff", "supervisor", "dispatcher"];

const STATUS: Record<string, { label: string; fill: string }> = {
  pending: { label: "Needs pricing", fill: "bg-fi-signal-amber" },
  priced: { label: "Priced", fill: "bg-fi-signal-blue" },
  waived: { label: "Waived", fill: "bg-fi-signal-slate" },
  billed: { label: "Billed", fill: "bg-fi-signal-green" },
};
const skinOf = (s: unknown) => (typeof s === "string" && Object.prototype.hasOwnProperty.call(STATUS, s) ? STATUS[s] : { label: "—", fill: "bg-fi-signal-slate" });
const str = (v: unknown): string | null => (v === null || v === undefined || v === "" ? null : String(v));

/** The upload keys a charge names; anything else (pre-api#1066 strings) is not an upload. */
const photoKeys = (photos: unknown[]): string[] => photos.filter((p): p is string => typeof p === "string" && p.trim() !== "");

export function DamageChargeRows({
  charges,
  canWrite,
  showOrder,
  timeZone,
}: {
  charges: RentalDamageCharge[];
  canWrite: boolean;
  showOrder?: boolean;
  timeZone: string | null;
}) {
  const [acting, setActing] = useState<{ charge: RentalDamageCharge; action: "price" | "waive" } | null>(null);
  const reopen = useChargeAction();

  return (
    <>
      <ul className="divide-y divide-fieldiq-border">
        {charges.map((c) => {
          const skin = skinOf(c.status);
          const photos = Array.isArray(c.photos) ? c.photos : [];
          return (
            <li key={c.id} data-testid="damage-charge" className="space-y-2 px-4 py-3 text-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <p className="font-semibold text-fieldiq-text">
                    {[str(c.asset_number), str(c.asset_name)].filter(Boolean).join(" · ") || "—"}
                    {showOrder && (
                      <>
                        {" · "}
                        <Link className="underline underline-offset-2" to={`/rentals/orders/${encodeURIComponent(c.order_id)}`}>
                          {str(c.order_number) ?? "Order"}
                        </Link>
                        {str(c.customer_name) ? ` · ${c.customer_name}` : ""}
                      </>
                    )}
                  </p>
                  <p className="whitespace-pre-line text-fieldiq-text" data-testid="damage-charge-description">{c.description}</p>
                  <p className="text-xs text-fieldiq-muted">
                    Found on return {formatInstant(str(c.inspection_date), timeZone) ?? "—"}
                    {str(c.inspector_name) ? ` by ${c.inspector_name}` : ""}
                    {" · "}
                    <Link className="underline underline-offset-2" to={`/rentals/inspections/${encodeURIComponent(c.inspection_id)}`}>Inspection</Link>
                    {photos.length > 0 && ` · ${photos.length} ${photos.length === 1 ? "photo" : "photos"}`}
                  </p>
                  {/* Copied from the inspection, so listed through its id with
                      signed URLs (api#1066). No photos: nothing is fetched. */}
                  {photos.length > 0 && <RentalInspectionPhotoList inspectionId={c.inspection_id} keys={photoKeys(photos)} />}
                  {c.status === "waived" && str(c.waive_reason) && (
                    <p className="text-xs text-fieldiq-text" data-testid="damage-charge-waive-reason">Waived: {c.waive_reason}</p>
                  )}
                  {c.status === "billed" && (
                    <p className="text-xs text-fieldiq-text" data-testid="damage-charge-billed">
                      On{" "}
                      {str(c.invoice_id) ? (
                        <Link className="underline underline-offset-2" to={`/rentals/invoices/${encodeURIComponent(String(c.invoice_id))}`}>
                          {str(c.invoice_number) ?? "its invoice"}
                        </Link>
                      ) : (
                        "an invoice"
                      )}
                      . Void that invoice to change it.
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <span className={`inline-block rounded-fi-pill border border-black/20 px-2 py-0.5 text-[0.62rem] font-black uppercase text-fi-signal-ink ${skin.fill}`}>{skin.label}</span>
                  <span className="font-mono text-sm font-extrabold tabular-nums text-fieldiq-text" data-testid="damage-charge-amount">
                    {c.status === "waived" ? "Waived" : fmtCurrency(parseMoney(c.amount))}
                  </span>
                </div>
              </div>
              {canWrite && c.status !== "billed" && (
                <div className="flex flex-wrap justify-end gap-2">
                  {(c.status === "pending" || c.status === "priced") && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => setActing({ charge: c, action: "waive" })}>Waive</Button>
                      <Button size="sm" onClick={() => setActing({ charge: c, action: "price" })}>{c.status === "priced" ? "Change price" : "Price"}</Button>
                    </>
                  )}
                  {c.status === "waived" && (
                    <Button size="sm" variant="outline" disabled={reopen.busy} onClick={async () => {
                      const err = await reopen.run(c, { action: "reopen" }, "Damage charge reopened");
                      if (err) toast.error(err);
                    }}>
                      Reopen
                    </Button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {acting && <DamageChargeDialog charge={acting.charge} action={acting.action} onClose={() => setActing(null)} />}
    </>
  );
}

/** Sends one PATCH and refreshes everything that shows charges or money from them. */
function useChargeAction() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const run = async (charge: RentalDamageCharge, body: RentalDamageChargeAction, done: string): Promise<string | null> => {
    setBusy(true);
    try {
      await api.rentals.damageCharges.update(charge.id, body);
      toast.success(done);
      qc.invalidateQueries({ queryKey: ["rental-damage-charges"] });
      qc.invalidateQueries({ queryKey: ["rentals-billing"] });
      qc.invalidateQueries({ queryKey: ["rentals-order"] });
      return null;
    } catch (error) {
      // The API's words; the caller shows them where the action was taken.
      return getApiErrorMessage(error, "The damage charge was not changed.");
    } finally {
      setBusy(false);
    }
  };
  return { run, busy };
}

function DamageChargeDialog({ charge, action, onClose }: { charge: RentalDamageCharge; action: "price" | "waive"; onClose: () => void }) {
  const { run, busy } = useChargeAction();
  const [amount, setAmount] = useState(charge.amount ?? "");
  const [reason, setReason] = useState("");
  const [refusal, setRefusal] = useState<string | null>(null);
  const isPrice = action === "price";
  // Blank is not a price; an explicit 0 is (the API takes it).
  const ready = isPrice ? String(amount).trim() !== "" : reason.trim() !== "";

  const submit = async () => {
    setRefusal(null);
    const body: RentalDamageChargeAction = isPrice ? { action: "price", amount: Number(amount) } : { action: "waive", reason: reason.trim() };
    const err = await run(charge, body, isPrice ? "Damage charge priced" : "Damage charge waived");
    if (err) setRefusal(err);
    else onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        {/* ui/dialog renders no dialog role; this one is labelled for assistive tech. */}
        <div role="dialog" aria-modal="true" aria-label={isPrice ? "Price damage charge" : "Waive damage charge"}>
        <DialogHeader>
          <DialogTitle>{isPrice ? "Price damage charge" : "Waive damage charge"}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-fieldiq-text">{[str(charge.asset_number), str(charge.asset_name)].filter(Boolean).join(" · ")}: {charge.description}</p>
        {isPrice ? (
          <div>
            <Label htmlFor="damage-amount">Amount</Label>
            <Input id="damage-amount" type="number" inputMode="decimal" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
            <p className="mt-1 text-xs text-fieldiq-muted">Billed on the next invoice, taxed as the rest.</p>
          </div>
        ) : (
          <div>
            <Label htmlFor="damage-reason">Reason</Label>
            <Textarea id="damage-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
            <p className="mt-1 text-xs text-fieldiq-muted">A waived charge bills nothing; it can be reopened.</p>
          </div>
        )}
        {refusal && (
          <p role="alert" data-testid="damage-charge-refusal" className="rounded-fi-control border border-fieldiq-border px-3 py-2 text-xs text-fieldiq-text">
            {refusal}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !ready}>
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            {isPrice ? "Save price" : "Waive"}
          </Button>
        </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
